import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FastingCalendar from '../components/FastingCalendar';
import CommitmentModal from '../components/CommitmentModal';
import DayViewModal from '../components/DayViewModal';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar, Users, Heart, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayStats, setDayStats] = useState(null);
  const [showDayView, setShowDayView] = useState(false);
  const [showCommitmentModal, setShowCommitmentModal] = useState(false);
  const [existingCommitments, setExistingCommitments] = useState([]);
  const [todayStats, setTodayStats] = useState({ count: 0 });
  const [upcomingFasts, setUpcomingFasts] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [todayRes, upcomingRes] = await Promise.all([
        axios.get(`${API_URL}/api/stats/today`),
        axios.get(`${API_URL}/api/stats/upcoming`)
      ]);
      setTodayStats(todayRes.data);
      setUpcomingFasts(upcomingRes.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleDateSelect = async (date, stats) => {
    setSelectedDate(date);
    setDayStats(stats);
    
    // Fetch existing commitments for the day
    try {
      const response = await axios.get(`${API_URL}/api/commitments/date/${date}`);
      setExistingCommitments(response.data);
    } catch (error) {
      setExistingCommitments([]);
    }
    
    setShowDayView(true);
  };

  const handleJoinClick = () => {
    if (!user) {
      toast.info('Please log in to commit to a fast');
      navigate('/login', { state: { returnTo: '/', date: selectedDate } });
      return;
    }
    setShowDayView(false);
    setShowCommitmentModal(true);
  };

  const handleCommitmentSuccess = () => {
    setRefreshKey(prev => prev + 1);
    fetchStats();
    setShowCommitmentModal(false);
    // Show day view again with updated data
    handleDateSelect(selectedDate, dayStats);
  };

  const formatUpcomingDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.getTime() === today.getTime()) return 'Today';
    if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
    
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="home-page">
      {/* Grain overlay */}
      <div className="grain-overlay" />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center max-w-3xl mx-auto animate-fade-in-up">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6">
              Pick a day. Commit to a fast. 
              <span className="text-primary"> Inspire others.</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              Join a quiet community of people turning personal intention into collective encouragement.
            </p>
            
            {/* Stats */}
            <div className="flex items-center justify-center gap-8 mb-12">
              <div className="text-center" data-testid="today-stats">
                <div className="flex items-center justify-center gap-2 text-primary mb-1">
                  <Users className="h-5 w-5" />
                  <span className="text-3xl font-display">{todayStats.count}</span>
                </div>
                <p className="text-sm text-muted-foreground">Fasting today</p>
              </div>
              <div className="w-px h-12 bg-border" />
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-accent mb-1">
                  <Heart className="h-5 w-5" />
                  <span className="text-3xl font-display">{upcomingFasts.reduce((sum, f) => sum + f.count, 0)}</span>
                </div>
                <p className="text-sm text-muted-foreground">Upcoming</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-border/50 shadow-lg p-6 sm:p-8 lg:p-12 animate-fade-in-up stagger-2">
          <FastingCalendar 
            key={refreshKey}
            onDateSelect={handleDateSelect} 
          />
        </div>
      </section>

      {/* Upcoming Fasts */}
      {upcomingFasts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-display text-2xl text-foreground mb-6 animate-fade-in-up stagger-3">
            Upcoming Fasting Days
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {upcomingFasts.map((fast, index) => (
              <button
                key={fast.date}
                onClick={() => handleDateSelect(fast.date, { count: fast.count })}
                className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-border/50 hover:border-primary/30 transition-all animate-fade-in-up text-left"
                style={{ animationDelay: `${0.4 + index * 0.1}s` }}
                data-testid={`upcoming-${fast.date}`}
              >
                <p className="font-medium text-foreground text-sm">{formatUpcomingDate(fast.date)}</p>
                <p className="text-primary font-display text-lg">{fast.count} {fast.count === 1 ? 'faster' : 'fasters'}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* CTA Section */}
      {!user && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-primary/5 rounded-2xl p-8 sm:p-12 text-center animate-fade-in-up stagger-4">
            <h2 className="font-display text-2xl sm:text-3xl text-foreground mb-4">
              Ready to join the community?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Create a free account to commit to fasts, share your intentions, and encourage others on their journey.
            </p>
            <Button
              onClick={() => navigate('/register')}
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg btn-lift"
              data-testid="cta-join-btn"
            >
              Join One Day Fast
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>
      )}

      {/* Modals */}
      <DayViewModal
        isOpen={showDayView}
        onClose={() => setShowDayView(false)}
        selectedDate={selectedDate}
        onJoinClick={handleJoinClick}
      />

      <CommitmentModal
        isOpen={showCommitmentModal}
        onClose={() => setShowCommitmentModal(false)}
        selectedDate={selectedDate}
        existingCommitments={existingCommitments}
        onSuccess={handleCommitmentSuccess}
      />
    </div>
  );
};

export default Home;
